import React from 'react'

import { Stack, List, ListItemButton, ListItemIcon, ListItemText, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, useTheme } from '@mui/material'
import { Button } from '@repo/ui'
import { CameraIcon, ImageIcon, XIcon } from 'lucide-react'

import { AvatarCropper } from './AvatarCropper'
import { useDispatch } from 'store/hooks'
import { updateProfile } from 'store/auth/actions'
import { useSnackbar } from 'components/Snackbar'
import { PICKER_SHEET } from 'components/UI/pickerListStyle'
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone'
import { Divider } from 'components/UI/Divider'

interface UpdatePhotoProps {
  handleClose: () => void
  onFileSelect: (file: File) => void
  onRemove?: () => void
}

export const UpdatePhoto = ({ handleClose, onFileSelect, onRemove }: UpdatePhotoProps) => {
  const theme = useTheme()
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [cropSrc, setCropSrc] = React.useState<string | null>(null)
  const [cameraOpen, setCameraOpen] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement | null>(null)
  const streamRef = React.useRef<MediaStream | null>(null)
  const dispatch = useDispatch()
  const { openSnackbar } = useSnackbar()

  const openCropper = (file: File) => {
    const reader = new FileReader()
    reader.onload = e => setCropSrc((e.target?.result as string) ?? null)
    reader.readAsDataURL(file)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      openCropper(file)
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
  }

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('mediaDevices not supported')
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      streamRef.current = stream
      setCameraOpen(true)
      setTimeout(() => {
        if (videoRef.current) {
          ;(videoRef.current as any).srcObject = stream
          videoRef.current.play().catch(() => {})
        }
      }, 0)
    } catch (_e) {
      // VRG-607 — "Camera access denied" is permission-system phrasing that
      // stops at the diagnosis. The reader cannot act on it from here: the
      // switch lives in their browser's site settings, so the copy has to say
      // so or it is a dead end.
      openSnackbar({
        severity: 'warning',
        title: "We can't reach your camera",
        text: 'Allow camera access for this site in your browser settings, then try again.',
      })
    }
  }

  const captureFromCamera = async () => {
    if (!videoRef.current) return
    const video = videoRef.current
    const canvas = document.createElement('canvas')
    const width = video.videoWidth || 720
    const height = video.videoHeight || 1280
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    stopCamera()
    setCameraOpen(false)
    setCropSrc(dataUrl)
  }

  return (
    <Stack sx={{ maxWidth: 480, width: '100%', mx: 'auto' }}>
      {!cropSrc && (
        <>
          <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
            {/* These two rows had NO `&:hover` of their own and fell through to MUI's
                default ListItemButton wash, which is the one flavour of row hover the
                app was trying to stop having several of. They now take the shared
                treatment: muted at rest, grey[50] + brand accent on hover AND
                keyboard focus. The glyphs move from a hardcoded black to
                `currentColor` so they travel with the row instead of staying black
                while the label beside them changes colour. */}
            <ListItemButton component="label" sx={{ py: PICKER_SHEET.itemPY, px: PICKER_SHEET.itemPX, my: PICKER_SHEET.itemGap, borderRadius: `${PICKER_SHEET.radius}px`, ...menuRowToneSx(theme) }}>
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                <ImageIcon size={24} strokeWidth={1} color="currentColor" />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ ...MENU_ROW_LABEL_SX, fontSize: '16px' }}>Choose from gallery</Typography>} />
              <input type="file" accept="image/*" hidden onChange={handleFileSelect} />
            </ListItemButton>
            <ListItemButton onClick={startCamera} sx={{ py: PICKER_SHEET.itemPY, px: PICKER_SHEET.itemPX, my: PICKER_SHEET.itemGap, borderRadius: `${PICKER_SHEET.radius}px`, ...menuRowToneSx(theme) }}>
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                <CameraIcon size={24} strokeWidth={1} color="currentColor" />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ ...MENU_ROW_LABEL_SX, fontSize: '16px' }}>Take photo</Typography>} />
            </ListItemButton>
          </List>
          {/*
            VERA-291 follow-up: "Remove photo" was a third ListItemButton
            styled identically to the two picker rows above, so it read as an
            equivalent choice rather than the distinct, destructive action it
            is. It's now a secondary destructive Button (the shared @repo/ui
            Button, working-conventions §32's "transient surface" tier — this
            drawer is a transient, single-purpose sheet the user opened to act
            on the photo, not persistent chrome, so it's always red rather
            than red-on-hover) directly under the divider with no extra top
            margin, so Divider's own baked-in 24px margin is the only gap
            between the picker rows and this action (per user direction:
            "closer to divider").
          */}
          <Divider />
          <Button
            variant="secondary"
            destructive
            fullWidth
            startIcon={<XIcon size={20} strokeWidth={1} />}
            onClick={() => setConfirmOpen(true)}
          >
            Remove photo
          </Button>
        </>
      )}

      {cropSrc && (
        <AvatarCropper
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onCropped={file => {
            onFileSelect(file)
            setCropSrc(null)
            dispatch(updateProfile({ avatar: file } as any, ({ success }) => {
              if (success) {
                openSnackbar({ severity: 'success', title: 'Photo updated' })
              }
            }))
            handleClose()
          }}
        />
      )}

  <Dialog 
    open={cameraOpen} 
    onClose={() => { setCameraOpen(false); stopCamera() }} 
    PaperProps={{ 
      sx: { 
        p: 0, 
        borderRadius: theme.shape.borderRadius, 
        backgroundColor: theme.palette.background.paper, 
        overflow: 'hidden',
      } 
    }} 
    fullWidth 
    maxWidth="xs"
  >
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '3/4', backgroundColor: theme.palette.common.black }}>
      <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </Box>
    <DialogActions sx={{ p: 2, display: 'flex', gap: 1 }}>
      <MuiButton
        onClick={() => { setCameraOpen(false); stopCamera() }}
        variant="outlined"
        fullWidth
        sx={{ borderRadius: theme.shape.borderRadius }}
      >
        Cancel
      </MuiButton>
      <MuiButton
        onClick={captureFromCamera}
        variant="contained"
        fullWidth
        sx={{ borderRadius: theme.shape.borderRadius, color: theme.palette.common.white }}
      >
        Capture
      </MuiButton>
    </DialogActions>
  </Dialog>

      <Dialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)} 
        PaperProps={{ 
          sx: { 
            p: 2, 
            borderRadius: theme.shape.borderRadius, 
            backgroundColor: theme.palette.background.paper,
          } 
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.primary, pb: 2, fontSize: '20px', textTransform: 'none' }}>
          Remove photo
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ color: theme.palette.text.primary, fontSize: '16px' }}>
            Are you sure you want to remove profile photo?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <MuiButton
            fullWidth
            variant="contained"
            sx={{
              fontSize: '16px',
              borderRadius: theme.shape.borderRadius,
              backgroundColor: theme.palette.error.main,
              color: theme.palette.common.white,
            }}
            onClick={() => { onRemove?.(); setConfirmOpen(false); handleClose(); }}
          >
            Remove
          </MuiButton>
          <MuiButton
            fullWidth
            variant="outlined"
            sx={{
              fontSize: '16px',
              borderRadius: theme.shape.borderRadius,
            }}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
